import { useEffect, useRef, useState, type RefObject } from 'react';

/** H15's fixed-portal rule: scroll-locked roots do not clip a viewport portal;
 * actual inner clips and roots establishing a containing block still do. */
export function visibleConversationContent(element: HTMLElement | null): boolean {
  if (!element || document.visibilityState !== 'visible' || !element.getClientRects().length) return false;
  const containsFixed = (root: HTMLElement) => {
    const style = getComputedStyle(root);
    return [style.transform, style.perspective, style.filter, style.backdropFilter].some(value => value && value !== 'none')
      || /\b(layout|paint|strict|content)\b/.test(style.contain)
      || /\b(transform|perspective|filter|backdrop-filter)\b/.test(style.willChange);
  };
  const portal = element.closest<HTMLElement>('[data-coach-modal="true"]');
  const viewportPortal = portal?.parentElement === document.body && getComputedStyle(portal).position === 'fixed'
    && !containsFixed(document.body) && !containsFixed(document.documentElement);
  const rect = element.getBoundingClientRect();
  let left = Math.max(0, rect.left), right = Math.min(innerWidth, rect.right);
  let top = Math.max(0, rect.top), bottom = Math.min(innerHeight, rect.bottom);
  for (let parent: HTMLElement | null = element; parent; parent = parent.parentElement) {
    const style = getComputedStyle(parent);
    if (parent.hidden || style.display === 'none' || ['hidden', 'collapse'].includes(style.visibility)) return false;
    if (parent === element || viewportPortal && (parent === document.body || parent === document.documentElement)) continue;
    const box = parent.getBoundingClientRect();
    if (/auto|scroll|hidden|clip/.test(style.overflowY)) { top = Math.max(top, box.top); bottom = Math.min(bottom, box.bottom); }
    if (/auto|scroll|hidden|clip/.test(style.overflowX)) { left = Math.max(left, box.left); right = Math.min(right, box.right); }
  }
  return right > left && bottom > top;
}

export function useFeedbackConversationVisibility(enabled: boolean, identity: string, ref: RefObject<HTMLElement>) {
  const [observed, setObserved] = useState<{ identity: string; visible: boolean } | null>(null);
  useEffect(() => {
    const check = () => setObserved({ identity, visible: enabled && visibleConversationContent(ref.current) });
    check();
    document.addEventListener('visibilitychange', check);
    document.addEventListener('scroll', check, true);
    window.addEventListener('resize', check);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(check);
    if (ref.current) observer?.observe(ref.current);
    return () => { document.removeEventListener('visibilitychange', check); document.removeEventListener('scroll', check, true);
      window.removeEventListener('resize', check); observer?.disconnect(); };
  }, [enabled, identity, ref]);
  return enabled && observed?.identity === identity && observed.visible;
}

/** A bounded proof range is eligible only after each message has appeared in
 * the visible pane. Scrolling a different range cannot satisfy this proof. */
export function useFeedbackTranscriptVisibility(enabled: boolean, identity: string, ref: RefObject<HTMLElement>, ids: readonly string[]) {
  const seen = useRef<{ identity: string; ids: Set<string> }>({ identity, ids: new Set() });
  const [complete, setComplete] = useState('');
  const key = ids.join(',');
  useEffect(() => {
    if (seen.current.identity !== identity) seen.current = { identity, ids: new Set() };
    const check = () => {
      if (!enabled || !identity || !visibleConversationContent(ref.current)) return;
      for (const element of ref.current?.querySelectorAll<HTMLElement>('[data-message-id]') ?? []) {
        if (visibleConversationContent(element) && element.dataset.messageId) seen.current.ids.add(element.dataset.messageId);
      }
      if (ids.length && ids.every(id => seen.current.ids.has(id))) setComplete(identity);
    };
    check(); document.addEventListener('scroll', check, true); document.addEventListener('visibilitychange', check); window.addEventListener('resize', check);
    return () => { document.removeEventListener('scroll', check, true); document.removeEventListener('visibilitychange', check); window.removeEventListener('resize', check); };
  }, [enabled, identity, key, ref]);
  return enabled && !!identity && complete === identity;
}
